/**
 * 檔案管理工具
 * 統一管理本機檔案的存放和匹配
 */

export interface FileMatchResult {
  success: boolean;
  filePath?: string;
  fileName?: string;
  needsUserSelection?: boolean;
  error?: string;
}

/**
 * 檢查檔案是否存在於預設目錄
 */
export async function checkFileExists(fileName: string): Promise<FileMatchResult> {
  try {
    // 嘗試多個可能的路徑（優先檢查 uploads 目錄）
    const possiblePaths = [
      `checkplay-videofile/uploads/${fileName}`,
      `checkplay-videofile/${fileName}`,
      `checkplay-videofile/imports/${fileName}`,
      `checkplay-videofile/exports/${fileName}`,
      fileName // 直接路徑
    ];
    
    for (const path of possiblePaths) {
      try {
        // 使用 fetch 檢查檔案是否存在，但只嘗試載入檔案頭部
        const response = await fetch(path, { 
          method: 'GET',
          headers: {
            'Range': 'bytes=0-0' // 只請求第一個字節
          }
        });
        
        // 檢查是否成功載入（200 或 206 狀態碼）且是影片檔案
        if ((response.ok || response.status === 206)) {
          const contentType = response.headers.get('content-type');
          console.log('🔍 檢查檔案響應:', { path, status: response.status, contentType });
          
          // 確保是影片檔案，不是 HTML 頁面
          if (contentType && (
            contentType.includes('video/') || 
            contentType.includes('application/octet-stream') ||
            contentType.includes('application/mp4')
          )) {
            console.log('✅ 檔案存在於路徑:', path);
            return {
              success: true,
              filePath: path,
              fileName: fileName
            };
          } else {
            console.log('❌ 檔案存在但類型不正確:', { path, contentType });
          }
        }
      } catch (error) {
        // 繼續嘗試下一個路徑
        console.log('❌ 路徑不可用:', path, error);
        continue;
      }
    }
    
    console.log('⚠️ 所有路徑都不可用，檔案不存在');
    return {
      success: false,
      fileName: fileName,
      needsUserSelection: true,
      error: '檔案不在預設目錄中'
    };
  } catch (error) {
    console.error('❌ 檢查檔案存在性時發生錯誤:', error);
    return {
      success: false,
      fileName: fileName,
      needsUserSelection: true,
      error: '無法檢查檔案存在性'
    };
  }
}

/**
 * 創建檔案選擇器並返回 File 對象
 */
export function createFileSelector(fileName: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.mp4,.m4v,.mpeg,.mov';
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        resolve(file);
      } else {
        reject(new Error('未選擇檔案'));
      }
    };
    
    input.oncancel = () => {
      reject(new Error('用戶取消選擇'));
    };
    
    input.click();
  });
}

/**
 * 智能檔案匹配系統
 * 1. 先嘗試從預設目錄載入
 * 2. 如果失敗，提示用戶選擇檔案
 */
export async function smartFileMatch(fileName: string): Promise<FileMatchResult> {
  console.log('🔍 智能檔案匹配開始:', fileName);
  
  // 步驟 1: 檢查預設目錄
  const checkResult = await checkFileExists(fileName);
  
  if (checkResult.success && checkResult.filePath) {
    console.log('✅ 在預設目錄找到檔案:', checkResult.filePath);
    
    try {
      // 載入檔案並創建 blob URL
      const response = await fetch(checkResult.filePath);
      if (response.ok) {
        const blob = await response.blob();
        
        // 確保正確的 MIME 類型
        let mimeType = blob.type;
        if (!mimeType || mimeType === 'application/octet-stream') {
          // 根據檔案副檔名設置 MIME 類型
          const extension = fileName.toLowerCase().split('.').pop();
          switch (extension) {
            case 'mp4':
              mimeType = 'video/mp4';
              break;
            case 'mov':
              mimeType = 'video/quicktime';
              break;
            case 'avi':
              mimeType = 'video/x-msvideo';
              break;
            case 'mkv':
              mimeType = 'video/x-matroska';
              break;
            default:
              mimeType = 'video/mp4'; // 預設
          }
        }
        
        const file = new File([blob], fileName, { type: mimeType });
        const blobUrl = createPlaybackUrl(file);
        
        console.log('✅ 成功創建 blob URL:', blobUrl, 'MIME 類型:', mimeType);
        
        return {
          success: true,
          filePath: blobUrl,
          fileName: fileName
        };
      } else {
        throw new Error('無法載入檔案');
      }
    } catch (error) {
      console.error('❌ 載入檔案失敗:', error);
      return {
        success: false,
        fileName: fileName,
        needsUserSelection: true,
        error: '無法載入檔案'
      };
    }
  }
  
  // 步驟 2: 提示用戶選擇檔案
  console.log('⚠️ 預設目錄中未找到檔案，需要用戶選擇');
  
  try {
    const selectedFile = await createFileSelector(fileName);
    console.log('✅ 用戶選擇了檔案:', selectedFile.name);
    
    // 為用戶選擇的檔案創建 blob URL
    const blobUrl = createPlaybackUrl(selectedFile);
    
    return {
      success: true,
      filePath: blobUrl,
      fileName: selectedFile.name
    };
  } catch (error) {
    console.error('❌ 檔案選擇失敗:', error);
    return {
      success: false,
      fileName: fileName,
      needsUserSelection: true,
      error: error instanceof Error ? error.message : '檔案選擇失敗'
    };
  }
}

/**
 * 從 File 對象創建播放 URL
 */
export function createPlaybackUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * 獲取檔案的修改日期
 * 對於本地檔案，嘗試從檔案系統獲取修改時間
 */
export async function getFileModificationDate(fileName: string): Promise<string> {
  try {
    // 嘗試從檔案系統獲取修改時間
    const response = await fetch(`checkplay-videofile/uploads/${fileName}`, {
      method: 'HEAD'
    });
    
    if (response.ok) {
      const lastModified = response.headers.get('last-modified');
      if (lastModified) {
        console.log('📅 獲取檔案修改日期:', lastModified);
        return new Date(lastModified).toISOString();
      }
    }
  } catch (error) {
    console.log('⚠️ 無法獲取檔案修改日期，使用當前時間:', error);
  }
  
  // 如果無法獲取檔案修改日期，使用當前時間
  return new Date().toISOString();
}

/**
 * 清理 blob URL
 */
export function cleanupBlobUrl(url: string): void {
  if (url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * 標準化檔案路徑
 * 移除路徑前綴，只保留檔案名
 */
export function normalizeFilePath(path: string): string {
  // 移除 blob: 前綴
  if (path.startsWith('blob:')) {
    return path;
  }
  
  // 移除路徑前綴，只保留檔案名
  const fileName = path.split('/').pop() || path;
  return fileName;
}

/**
 * 驗證檔案類型
 */
export function validateFileType(file: File): boolean {
  const validTypes = ['video/mp4', 'video/x-m4v', 'video/mpeg', 'video/quicktime'];
  return validTypes.includes(file.type.toLowerCase());
}

/**
 * 獲取檔案顯示名稱
 */
export function getFileDisplayName(path: string): string {
  return normalizeFilePath(path);
}
